package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private val list = Bem("admin-selectable-list")
private val reorderBlock = Bem("admin-reorder")

/**
 * A row: [thumbnailUrl] is the record's main image, [badge] usually an [AdminBadge] with its status. A [logo] thumbnail
 * shows the whole image on white, such as a sponsor's logo; without an image it is the usual grey placeholder.
 */
class SelectableRow<K : Any>(
    val key: K,
    val title: String,
    val meta: String,
    val thumbnailUrl: String? = null,
    val badge: (@Composable () -> Unit)? = null,
    val logo: Boolean = false,
)

/**
 * The left half of a list-and-editor screen. Selecting a row is the consumer's job, including asking first when the
 * editor has unsaved changes (see [UnsavedChangesGuard]). With [reorder], each row gets a handle to sort it by hand.
 * [recent] marks the row that was open last, back on the list (see [AdminSplitView]).
 */
@Composable
fun <K : Any> AdminSelectableList(
    rows: List<SelectableRow<K>>,
    selected: K?,
    onSelect: (K) -> Unit,
    recent: K? = null,
    reorder: ReorderController<K>? = null,
    reorderLabel: (SelectableRow<K>) -> String = { "${it.title} verschieben" },
) {
    Ul({ classes(*listOfNotNull(list.block, reorderBlock.block.takeIf { reorder != null }).toTypedArray()) }) {
        rows.forEach { row ->
            key(row.key) {
                Li({
                    classes(list.element("item").className)
                    if (reorder != null) {
                        // a mix with the reorder block, which lays out the handle beside the row
                        classes(reorderBlock.element("item").className)
                        reorderItem(reorder, row.key)
                    }
                }) {
                    reorder?.let { ReorderHandle(it, row.key, reorderLabel(row)) }
                    Button({
                        type(ButtonType.Button)
                        classes(list.element("row").className)
                        attr("aria-pressed", (row.key == selected).toString())
                        if (row.key == recent) {
                            attr("data-recent", "true")
                        }
                        onClick { onSelect(row.key) }
                    }) {
                        val thumb = list.element("thumb")
                        if (row.thumbnailUrl != null) {
                            Img(row.thumbnailUrl, attrs = {
                                classes(*listOfNotNull(thumb.className, thumb.modifier("logo").takeIf { row.logo }).toTypedArray())
                                alt("")
                                attr("loading", "lazy")
                            })
                        } else {
                            Span({
                                classes(thumb.className)
                                attr("aria-hidden", "true")
                            })
                        }
                        Span({ classes(list.element("text").className) }) {
                            Span({ classes(list.element("title").className) }) { Text(row.title) }
                            Span({ classes(list.element("meta").className) }) { Text(row.meta) }
                        }
                        row.badge?.invoke()
                    }
                }
            }
        }
    }
}
